import { Gauge, Package, Image as ImageIcon, Activity, Truck, CheckCircle2, AlertTriangle, ShieldCheck, ShieldAlert } from "lucide-react";

const STATUS = {
  ok: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700", label: "Healthy" },
  watch: { bar: "bg-amber-500", text: "text-amber-700", chip: "bg-amber-100 text-amber-700", label: "Approaching" },
  act: { bar: "bg-[#B01E28]", text: "text-[#B01E28]", chip: "bg-red-100 text-[#B01E28]", label: "Action needed" },
};
const COUNT_META = {
  parts: { icon: Package, label: "Parts" },
  photos: { icon: ImageIcon, label: "Photos" },
  transactions: { icon: Activity, label: "Transactions" },
  donors: { icon: Truck, label: "Trucks" },
};

const fmt = (n) => (n ?? 0).toLocaleString();

export function ScaleHealthCard({ scale }) {
  if (!scale) return null;
  const overall = STATUS[scale.overall] || STATUS.ok;
  return (
    <div data-testid="scale-health-card" className="border border-slate-200 bg-white rounded-xl shadow-sm mb-5 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <Gauge className="w-4 h-4 text-[#B01E28]" />
        <h2 className="font-display font-bold text-base">Scale &amp; performance health</h2>
        <span data-testid="scale-overall" className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${overall.chip}`}>{overall.label}</span>
      </div>

      <div className="p-4">
        {/* live counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {Object.entries(COUNT_META).map(([key, m]) => {
            const Icon = m.icon;
            return (
              <div key={key} data-testid={`scale-count-${key}`} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-400"><Icon className="w-3.5 h-3.5" />{m.label}</div>
                <div className="font-display text-2xl font-black text-[#1F1F1F] mt-1 tabular-nums">{fmt(scale.counts?.[key])}</div>
              </div>
            );
          })}
        </div>

        {/* threshold gauges */}
        <div className="space-y-3.5">
          {(scale.thresholds || []).map((t) => {
            const s = STATUS[t.status] || STATUS.ok;
            return (
              <div key={t.key} data-testid={`scale-threshold-${t.key}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700">{t.label}</span>
                  <span className={`font-mono text-xs ${s.text}`} data-testid={`scale-threshold-${t.key}-value`}>
                    {fmt(t.current)} / {fmt(t.trigger)} {t.unit} · {t.pct}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${s.bar} transition-all`} style={{ width: `${Math.max(1.5, t.pct)}%` }} />
                </div>
                {t.status !== "ok" && (
                  <p className={`text-[11px] mt-1 ${s.text}`} data-testid={`scale-threshold-${t.key}-action`}>
                    <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />At the trigger: {t.action}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* index health */}
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-2">
            {scale.index_health_ok
              ? <ShieldCheck className="w-4 h-4 text-emerald-600" />
              : <ShieldAlert className="w-4 h-4 text-[#B01E28]" />}
            <span className="text-sm font-medium text-slate-700">Scale indexes (m0029)</span>
            <span data-testid="scale-index-health" className={`ml-auto text-[11px] font-semibold ${scale.index_health_ok ? "text-emerald-700" : "text-[#B01E28]"}`}>
              {scale.index_health_ok ? "All present ✓" : "MISSING — rerun migrations"}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(scale.indexes || []).map((ix) => (
              <span key={ix.name} data-testid={`scale-index-${ix.name}`}
                className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full ${ix.present ? "bg-slate-100 text-slate-600" : "bg-red-100 text-[#B01E28]"}`}>
                {ix.present ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3" />}
                {ix.collection}.{ix.name}
              </span>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-3">{scale.note}</p>
      </div>
    </div>
  );
}
